import {
  DimensionType,
  FunnelStage,
  MetricSlug,
  PeriodType,
  PoleClassification,
  Region,
  RegionEnum,
} from '../api/theHive/redeProto/types';

/** Mapa UF → região (network_data.json não traz a região). */
export const UF_REGION: Record<string, Region> = {
  AC: 'Norte',
  AP: 'Norte',
  AM: 'Norte',
  PA: 'Norte',
  RO: 'Norte',
  RR: 'Norte',
  TO: 'Norte',
  AL: 'Nordeste',
  BA: 'Nordeste',
  CE: 'Nordeste',
  MA: 'Nordeste',
  PB: 'Nordeste',
  PE: 'Nordeste',
  PI: 'Nordeste',
  RN: 'Nordeste',
  SE: 'Nordeste',
  DF: 'Centro-Oeste',
  GO: 'Centro-Oeste',
  MT: 'Centro-Oeste',
  MS: 'Centro-Oeste',
  ES: 'Sudeste',
  MG: 'Sudeste',
  RJ: 'Sudeste',
  SP: 'Sudeste',
  PR: 'Sul',
  RS: 'Sul',
  SC: 'Sul',
};

export const REGIONS: Region[] = [
  'Norte',
  'Nordeste',
  'Centro-Oeste',
  'Sudeste',
  'Sul',
];

/**
 * UF como código IntEnum (backend serializa `uf` como int, não sigla).
 * Fonte: seção 4 do handoff de integração.
 */
export const UF_SIGLA_BY_INT: Record<number, string> = {
  1: 'AC',
  2: 'AL',
  3: 'AP',
  4: 'AM',
  5: 'BA',
  6: 'CE',
  7: 'DF',
  8: 'ES',
  9: 'GO',
  10: 'MA',
  11: 'MT',
  12: 'MS',
  13: 'MG',
  14: 'PA',
  15: 'PB',
  16: 'PR',
  17: 'PE',
  18: 'PI',
  19: 'RJ',
  20: 'RN',
  21: 'RS',
  22: 'RO',
  23: 'RR',
  24: 'SC',
  25: 'SP',
  26: 'SE',
  27: 'TO',
};

/** Sigla → código IntEnum (para enviar `uf` ao backend em filtros/metas). */
export const UF_INT_BY_SIGLA: Record<string, number> = Object.entries(
  UF_SIGLA_BY_INT
).reduce<Record<string, number>>((acc, [code, sigla]) => {
  acc[sigla] = Number(code);
  return acc;
}, {});

/** Converte o código IntEnum do backend em sigla; fallback para o próprio valor. */
export const ufSiglaFromInt = (uf: number | null | undefined): string =>
  uf == null ? '' : UF_SIGLA_BY_INT[uf] ?? String(uf);

/** Região: enum minúsculo do backend → label (domínio das telas). */
export const REGION_LABEL_BY_ENUM: Record<RegionEnum, Region> = {
  norte: 'Norte',
  nordeste: 'Nordeste',
  centro_oeste: 'Centro-Oeste',
  sudeste: 'Sudeste',
  sul: 'Sul',
};

/** Região: label (domínio) → enum minúsculo (para enviar ao backend). */
export const REGION_ENUM_BY_LABEL: Record<Region, RegionEnum> = {
  Norte: 'norte',
  Nordeste: 'nordeste',
  'Centro-Oeste': 'centro_oeste',
  Sudeste: 'sudeste',
  Sul: 'sul',
};

/** Converte o enum de região do backend em label; `null` quando ausente. */
export const regionLabelFromEnum = (
  region: string | null | undefined
): Region | null =>
  region == null ? null : REGION_LABEL_BY_ENUM[region as RegionEnum] ?? null;

/** Motivos de rejeição (enum inglês do backend) → rótulo pt-BR. */
export const REJECTION_REASON_LABEL: Record<string, string> = {
  inadequate_research_sample: 'Amostra de pesquisa inadequada',
  area_calculation_error: 'Erro de cálculo de área',
  low_quality_photos: 'Fotos de baixa qualidade',
  incomplete_report: 'Laudo incompleto',
  missing_art: 'Ausência de ARTs',
  deadline_exceeded_without_justification:
    'Prazo extrapolado sem justificativa',
  value_inconsistency: 'Inconsistência de valores',
  incomplete_inspection: 'Vistoria incompleta',
  date_inconsistency: 'Inconsistência de datas',
  insufficient_comparative_method: 'Método comparativo insuficiente',
  other: 'Outro',
};

/** Tipos de imóvel (enum inglês do backend) → rótulo pt-BR. */
export const REAL_ESTATE_TYPE_LABEL: Record<string, string> = {
  house: 'Casa',
  apartment: 'Apartamento',
  land: 'Terreno',
  commercial_space: 'Espaço comercial',
  store: 'Loja',
  lot: 'Lote',
  shed: 'Galpão',
  commercial_building: 'Prédio comercial',
  loft: 'Loft',
  flat: 'Flat',
  enterprise: 'Empreendimento',
};

export const rejectionReasonLabel = (reason: string): string =>
  REJECTION_REASON_LABEL[reason] ?? reason;

export const realEstateTypeLabel = (type: string): string =>
  REAL_ESTATE_TYPE_LABEL[type] ?? type;

/**
 * Centroides aproximados por UF (lat/lng). Os JSON mock não trazem coordenadas
 * de polo/município, então usamos o centroide da UF como âncora e dispersamos
 * os polos ao redor dele (ver useNetworkMap).
 */
export const UF_CENTROID: Record<string, { lat: number; lng: number }> = {
  AC: { lat: -8.77, lng: -70.55 },
  AL: { lat: -9.62, lng: -36.82 },
  AP: { lat: 1.41, lng: -51.77 },
  AM: { lat: -3.47, lng: -65.1 },
  BA: { lat: -12.96, lng: -41.7 },
  CE: { lat: -5.2, lng: -39.53 },
  DF: { lat: -15.83, lng: -47.86 },
  ES: { lat: -19.19, lng: -40.34 },
  GO: { lat: -15.98, lng: -49.86 },
  MA: { lat: -5.42, lng: -45.44 },
  MT: { lat: -12.64, lng: -55.42 },
  MS: { lat: -20.51, lng: -54.54 },
  MG: { lat: -18.1, lng: -44.38 },
  PA: { lat: -3.79, lng: -52.48 },
  PB: { lat: -7.28, lng: -36.72 },
  PR: { lat: -24.89, lng: -51.55 },
  PE: { lat: -8.38, lng: -37.86 },
  PI: { lat: -6.6, lng: -42.28 },
  RJ: { lat: -22.25, lng: -42.66 },
  RN: { lat: -5.81, lng: -36.59 },
  RS: { lat: -30.17, lng: -53.5 },
  RO: { lat: -10.83, lng: -63.34 },
  RR: { lat: 1.99, lng: -61.33 },
  SC: { lat: -27.45, lng: -50.95 },
  SP: { lat: -22.19, lng: -48.79 },
  SE: { lat: -10.57, lng: -37.45 },
  TO: { lat: -9.46, lng: -48.26 },
};

/** Centro geográfico do Brasil (para centralizar o mapa). */
export const BRAZIL_CENTER = { lat: -14.24, lng: -51.93 };

export const FUNNEL_STAGE_LABEL: Record<FunnelStage, string> = {
  lead: 'Lead identificado',
  contacted: 'Contatado',
  registering: 'Em cadastro',
  docs_pending: 'Documentação pendente',
  validating: 'Em validação',
  approved: 'Aprovado',
  active: 'Ativo',
  rejected: 'Reprovado',
  inactive: 'Inativo',
};

/** Estágios do pipeline principal, em ordem (exclui terminais). */
export const FUNNEL_PIPELINE: FunnelStage[] = [
  'lead',
  'contacted',
  'registering',
  'docs_pending',
  'validating',
  'approved',
  'active',
];

export const METRIC_SLUG_LABEL: Record<MetricSlug, string> = {
  credenciados_ativos: 'Credenciados ativos',
  novos_credenciados: 'Novos credenciados',
  captacao: 'Captação',
  csat: 'CSAT',
};

export const DIMENSION_TYPE_LABEL: Record<DimensionType, string> = {
  geral: 'Geral',
  uf: 'UF',
  regiao: 'Região',
  polo: 'Polo',
  tipologia: 'Tipologia',
};

export const PERIOD_TYPE_LABEL: Record<PeriodType, string> = {
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  anual: 'Anual',
};

/**
 * Limiares de classificação de polos. CSAT está na escala 0–5 (como no mock);
 * SLA, atingimento e aprovação em percentual (0–100).
 */
export const THRESHOLDS = {
  sla: { critical: 80, healthy: 90, excellent: 95 },
  csat: { critical: 3.5, healthy: 4.0, excellent: 4.5 },
  achievement: { critical: 50, healthy: 80, excellent: 100 },
  approval: { critical: 60, healthy: 75, excellent: 90 },
  /** Limiares genéricos de nota 0–100 (usados nos pilares do BSC). */
  score: { critical: 50, healthy: 75, excellent: 90 },
} as const;

export const PILLAR_LABEL = {
  health: 'Saúde',
  growth: 'Crescimento',
  experience: 'Experiência',
  result: 'Resultado',
} as const;

export const CLASSIFICATION_LABEL: Record<PoleClassification, string> = {
  critical: 'Crítico',
  attention: 'Atenção',
  healthy: 'Saudável',
  excellent: 'Excelente',
};

/** Ordem de severidade (maior = mais grave) — para ordenação de listas. */
export const CLASSIFICATION_SEVERITY: Record<PoleClassification, number> = {
  critical: 3,
  attention: 2,
  healthy: 1,
  excellent: 0,
};

export const Constants = {
  title: 'Dashboard credenciado',
  subtitle: 'Inteligência da rede de credenciados',
  tabs: {
    overview: 'Visão Geral',
    bsc: 'BSC',
    map: 'Mapa',
    criticalPoles: 'Polos Críticos',
    customerSuccess: 'Customer Success',
    evolution: 'Evolução',
    funnel: 'Captação',
    typologies: 'Tipologias',
    goals: 'Metas',
  },
  kpis: {
    activeAccredited: 'Credenciados ativos',
    goal: 'Meta atual',
    coverage: '% Cobertura',
    criticalPoles: 'Polos críticos',
    sla: 'SLA da rede',
    csat: 'CSAT médio',
    firstReviewApproval: 'Aprovação 1ª revisão',
    regionsBelowGoal: 'Regiões abaixo da meta',
  },
  pillars: {
    health: 'Saúde',
    growth: 'Crescimento',
    experience: 'Experiência',
    result: 'Resultado',
  },
  loading: 'Carregando dados da rede...',
  empty: 'Nenhum dado disponível.',
  error: 'Não foi possível carregar os dados da rede.',
} as const;

/** Base da rota da Rede Proto (aninhada em The Hive). */
export const REDE_PROTO_BASE = '/the-hive/rede-proto';

/**
 * Sub-navegação interna da Rede Proto. `path` é relativo à base acima
 * (`''` = tela inicial / Visão Geral).
 */
export const SUBNAV: { path: string; label: string }[] = [
  { path: '', label: Constants.tabs.overview },
  { path: 'bsc', label: Constants.tabs.bsc },
  { path: 'mapa', label: Constants.tabs.map },
  { path: 'polos-criticos', label: Constants.tabs.criticalPoles },
  { path: 'customer-success', label: Constants.tabs.customerSuccess },
  { path: 'evolucao', label: Constants.tabs.evolution },
  { path: 'captacao', label: Constants.tabs.funnel },
  { path: 'tipologias', label: Constants.tabs.typologies },
  { path: 'metas', label: Constants.tabs.goals },
];
